/**
 * Enhanced Input component with consistent validation and error handling
 */

import React from 'react';
import {
  Input as ChakraInput,
  InputProps as ChakraInputProps,
  FormControl,
  FormLabel,
  FormErrorMessage,
  FormHelperText,
  InputGroup,
  InputLeftElement,
  InputRightElement,
  forwardRef,
} from '@chakra-ui/react';
import { BaseComponentProps, ComponentSize } from '../../types';

export interface InputProps
  extends BaseComponentProps,
    Omit<ChakraInputProps, 'size'> {
  /** Input label */
  label?: string;
  /** Input size */
  size?: ComponentSize;
  /** Helper text */
  helperText?: string;
  /** Error message */
  error?: string;
  /** Whether field is required */
  required?: boolean;
  /** Left icon element */
  leftIcon?: React.ReactElement;
  /** Right icon element */
  rightIcon?: React.ReactElement;
  /** Whether to show required asterisk */
  showRequiredIndicator?: boolean;
}

export const Input = forwardRef<InputProps, 'input'>((props, ref) => {
  const {
    label,
    size = 'md',
    helperText,
    error,
    required = false,
    leftIcon,
    rightIcon,
    showRequiredIndicator = true,
    className,
    testId,
    ...rest
  } = props;

  const isInvalid = Boolean(error);

  const inputElement = (
    <ChakraInput
      ref={ref}
      size={size}
      isInvalid={isInvalid}
      data-testid={testId}
      {...rest}
    />
  );

  const wrappedInput =
    leftIcon || rightIcon ? (
      <InputGroup size={size}>
        {leftIcon && <InputLeftElement>{leftIcon}</InputLeftElement>}
        {inputElement}
        {rightIcon && <InputRightElement>{rightIcon}</InputRightElement>}
      </InputGroup>
    ) : (
      inputElement
    );

  if (label || error || helperText) {
    return (
      <FormControl
        isRequired={required}
        isInvalid={isInvalid}
        className={className}
      >
        {label && (
          <FormLabel>
            {label}
            {required && showRequiredIndicator && (
              <span style={{ color: 'red', marginLeft: '4px' }}>*</span>
            )}
          </FormLabel>
        )}
        {wrappedInput}
        {error ? (
          <FormErrorMessage>{error}</FormErrorMessage>
        ) : (
          helperText && <FormHelperText>{helperText}</FormHelperText>
        )}
      </FormControl>
    );
  }

  return wrappedInput;
});

Input.displayName = 'Input';
